import { getTextStyle } from "cms/theme/helpers";
import Styled from "styled-components";

export const Container = Styled.div`
  position: relative;
  padding: 2rem 0;
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("medium")} {
    padding: 4.6875rem 0;
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    padding: 5.625rem 0;
  }
`;

export type Variant = "light" | "dark";
export interface BackgroundImageProps {
  $variant: Variant;
}

export const BackgroundImage = Styled.picture<BackgroundImageProps>`
  display: block;
  position: absolute;
  z-index: -2;
  height: 100%;
  width: 100%;
  top: -60%;
  left: -8%;

  img {
    width: 160%;
    height: 160%;
  }

  ${(props) =>
    props.$variant === "dark" &&
    `
    top: -22%;
    left: 0%;

    img {
      width: 170%;
      height: 130%;
    }
  `}
`;

export interface BackgroundNoiseProps {
  $backgroundNoise: string;
  $variant: Variant;
}

export const BackgroundNoise = Styled.div<BackgroundNoiseProps>`
  display: block;
  position: absolute;
  z-index: -1;
  height: 100%;
  width: 100%;
  opacity: 5%;
  background: repeat url(${(props) => props.$backgroundNoise});
  -webkit-mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.3) 25%, rgba(0, 0, 0, 0.8) 75%, rgba(0, 0, 0, 1) 100%);
  mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.3) 25%, rgba(0, 0, 0, 0.8) 75%, rgba(0, 0, 0, 1) 100%);
  background-blend-mode: overlay;

  ${(props) =>
    props.$variant === "dark" &&
    `
    height: 120%;
    top: -22%;
    opacity: 1%;
    -webkit-mask-image: linear-gradient(360deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4430147058823529) 15%, rgba(0,0,0,1) 100%);
    mask-image: linear-gradient(360deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4430147058823529) 15%, rgba(0,0,0,1) 100%);
  `}
`;

export const Content = Styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 78.75rem;
  max-width: calc(100% - 2.5rem);
  margin: 0 auto;
  gap: 2.5rem;
  z-index: 1;

  ${(props) => props.theme.breakpoints.up("medium")} {
    max-width: calc(100% - 3rem);
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    max-width: calc(100% - 10rem);
    gap: 4rem
  }
`;

export const Message = Styled.div`
  position: relative;
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;

  ${(props) => props.theme.breakpoints.up("medium")} {
    gap: 2rem;
    max-width: 45rem;
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    max-width: 62.5rem;
  }
`;

export interface IconCursorProps {
  $position: {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  };
}

export const IconCursorContainer = Styled.div<IconCursorProps>`
  display: none;
  position: absolute;
  width: auto;
  height: 2.375rem;
  top: ${(props) => props.$position.top || "auto"};
  bottom: ${(props) => props.$position.bottom || "auto"};
  left: ${(props) => props.$position.left || "auto"};
  right: ${(props) => props.$position.right || "auto"};

  & > img {
    width: 100%;
    height: 100%;
  }

  ${(props) => props.theme.breakpoints.up("large")} {
     display: block;
  }
`;

export interface HeadlineContainerProps {
  $variant: Variant;
}

export const HeadlineContainer = Styled.div<HeadlineContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;

  ${(props) =>
    props.$variant === "dark" &&
    `
    background: linear-gradient(78deg, #FFF 9.07%, rgba(255, 255, 255, 0.90) 50%, rgba(255, 255, 255, 0.50) 90.93%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  `};
`;

export const TaglineContainer = Styled.div`
  text-align: center;

  p {
    font-weight: ${(props) => props.theme.text.variants.D1.weights.medium};
  }
`;

export interface SearchContainerProps {
  $variant: Variant;
}

export const SearchContainer = Styled.div<SearchContainerProps>`
  position: relative;
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("medium")} {
    flex-direction: row;
    gap: 0;
    width: 35rem;
    border: 1px solid #E8E8E8;
    border-radius: 0.75rem;
    background-color: #FFFFFF;

    ${(props) =>
      props.$variant === "dark" &&
      `
      border: none;
    `}
  }
`;

export const InputContainer = Styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
`;

export const Input = Styled.input`
  display: block;
  width: 100%;
  height: 2.625rem;
  padding: 0.75rem;
  font-size: ${(props) => props.theme.text.variants.L3.viewport.tablet.fontSize};
  font-weight: ${(props) => props.theme.text.variants.L3.weights.medium};
  line-height: ${(props) =>
    props.theme.text.variants.L3.viewport.tablet.lineHeight};
  background-color: #FFFFFF;
  border: 1px solid #E8E8E8;
  border-radius: 0.625rem;
  color: ${(props) => props.theme.colors.neutrals.gray900};
  outline: none;
  transition: .3s;

  ${(props) => props.theme.breakpoints.up("medium")} {
    height: 3rem;
    border-color: transparent;
    flex: 0.7;
  }

  &:focus + div {
    display: none;
  }
`;

export interface PlaceholderProps {
  $hasInput: boolean;
}

export const Placeholder = Styled.div<PlaceholderProps>`
  display: flex;
  align-items: center;
  padding: 0 0.75rem;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  color: #232323;
  opacity: 0.5;
  white-space: nowrap;

  ${(props) => getTextStyle(props.theme, "L3")};

  ${(props) =>
    props.$hasInput &&
    `
    display: none;
  `}
`;

export const ButtonContainer = Styled.div`
  width: 100%;

  ${(props) => props.theme.breakpoints.up("medium")} {
    width: auto;
    flex: 0.3;
  }
`;

export const Link = Styled.a`
  text-decoration: none;
`;

export interface IllustrationContainerProps {
  $variant: Variant;
}

export const IllustrationContainer = Styled.div<IllustrationContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("medium")} {
    border: 2px solid rgba(255, 255, 255, 0.40);
    background: rgba(255, 255, 255, 0.30);
    background-blend-mode: luminosity;
    backdrop-filter: blur(50px);
    padding: 1.25rem;
    border-radius: 1rem;

    ${(props) =>
      props.$variant === "dark" &&
      `
      border-radius: 16px;
      border: 2px solid rgba(255, 255, 255, 0.32);
      background: rgba(33, 33, 33, 0.32);
      background-blend-mode: luminosity;
      backdrop-filter: blur(50px);
    `}
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    padding: 1.5rem;
    margin-top: 1rem;
    width: auto;
  }
`;

export interface IllustrationProps {
  $variant: Variant;
}

export const Illustration = Styled.div<IllustrationProps>`
  width: 100%;
  border-radius: 1rem;
  box-shadow: 0px 8px 40px 0px rgba(3, 3, 3, 0.10), 0px 16px 16px -16px rgba(3, 3, 3, 0.16);
  
  ${(props) =>
    props.$variant === "dark" &&
    `
    box-shadow: 0px 10.679px 33.371px 0px rgba(0, 0, 0, 0.55);
  `}

  .gatsby-image-wrapper {
    border-radius: 0.25rem;
  }

  ${(props) => props.theme.breakpoints.up("medium")} {
    border-radius: 0.625rem;

    .gatsby-image-wrapper {
      border-radius: 0.625rem;
    }
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    border-radius: 0.75rem;

    .gatsby-image-wrapper {
      border-radius: 0.75rem;
    }
  }
`;

export const VideoWrapper = Styled.div`
  position: relative;
  overflow: hidden;
  width: 960px;
  max-width: 100%;
  height: 0px;
  margin: 0px auto;
  padding-bottom: 60%;
  border-radius: 1rem;
  box-shadow: rgba(68, 20, 187, 0.75) 0px -20px 200px;
  cursor: pointer;

  video {
    position: absolute;
    z-index: 1;
    left: 0px;
    width: 100%;
  }

  ${(props) => props.theme.breakpoints.up("medium")} {
    padding-bottom: 53%;
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    padding-bottom: 56.25%;
  }
`;

export const VideoContainer = Styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

export const VideoFrame = Styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

export interface YouTubeIframeProps {
  $allowfullscreen: boolean;
}

export const YoutubeIframe = Styled.iframe<YouTubeIframeProps>`
  overflow: hidden;
  border-radius: 1rem;
  border: none;
  box-shadow: 0px -20px 200px rgba(68, 20, 187, 0.5);
`;

export const CloseButton = Styled.button`
  background-color: transparent;
  border: none;
  position: absolute;
  top: 20px !important;
  right: 20px !important;
  cursor: pointer;
`;

export const CompaniesLogoScrollerWrapper = Styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  width: 100%;
  padding: 1.5rem 0 1rem;

  ${(props) => props.theme.breakpoints.up("medium")} {
    gap: 2rem;
    padding: 2.5rem 0 1rem;
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    padding: 3rem 0 1.375rem;
  }
`;

export const LogosHeadlineContainer = Styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CompanyLogoScroller = Styled.div`
  width: 100%;
  overflow: hidden;
  padding: 1rem 0;
  -webkit-mask-image: linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgb(0, 0, 0) 25%, rgb(0, 0, 0) 75%, rgba(0, 0, 0, 0) 100%);
  mask-image: linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgb(0, 0, 0) 25%, rgb(0, 0, 0) 75%, rgba(0, 0, 0, 0) 100%);
  white-space: nowrap;
`;

export const CompanyLogoScrollerInner = Styled.div`
  display: inline-block;
  animation: scrollX 35s linear infinite;
  width: max-content;

  @keyframes scrollX {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(-100%);
    }
  }
`;

export const Logo = Styled.img`
  display: inline-block;
  height: 2rem;
  margin: 0 1.25rem;

  ${(props) => props.theme.breakpoints.up("medium")} {
    height: 2.5rem;
    margin: 0 1.5rem;
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    height: 3.5rem;
    margin: 0 2rem;
  }
`;

export const LogoWrapper = Styled.div`
  display: inline-block;
  margin: 0 1.25rem;

  img {
    height: 2rem;
  }

  ${(props) => props.theme.breakpoints.up("medium")} {
    margin: 0 1.5rem;

    img {
      height: 2.5rem;
    }
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    margin: 0 2rem;

    img {
      height: 3.5rem;
    }
  }
`;
